/**
 * Premium subscription + manual-payment domain.
 *
 * Premium is granted either permanently (admin/owner sets `isPremium` with no
 * expiry) or for a duration via an approved payment request. The EFFECTIVE plan
 * is always derived through the policy layer (see resolvePlan) — this module
 * only defines the payment/entitlement shapes and the pure, well-tested math for
 * expiry + extension. Prices live in a server-side config and are served to the
 * client; they are never hardcoded in the frontend.
 */
import { z } from 'zod';
import type { Plan, PremiumTier } from './policy.js';

/** One day in milliseconds. */
export const DAY_MS = 86_400_000;

/** How a Premium purchase is paid for. */
export const PAYMENT_METHODS = ['TOMAN_MANUAL', 'TELEGRAM_STARS'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Payment-request lifecycle. Explicit so the backend can enforce legal
 * transitions and never double-activate Premium. Two methods share one model:
 *
 *  Manual Toman (TOMAN_MANUAL):
 *   AWAITING_RECEIPT  — session started; limited window to submit a receipt.
 *   RECEIPT_SUBMITTED — receipt sent; waiting for admin review.
 *   APPROVED          — admin approved; Premium activated/extended (terminal).
 *   REJECTED          — admin rejected; no Premium (terminal).
 *   CANCELLED         — user cancelled before submitting (terminal).
 *
 *  Telegram Stars (TELEGRAM_STARS):
 *   AWAITING_PAYMENT  — invoice created; waiting for Telegram successful_payment.
 *   COMPLETED         — payment verified server-side; Premium auto-activated (terminal).
 *   REFUNDED          — Stars refunded by owner/Telegram (terminal).
 *   FAILED            — payment failed/declined (terminal).
 *
 *  Shared:
 *   EXPIRED           — window elapsed before completion (terminal).
 */
export const PAYMENT_STATUSES = [
  'AWAITING_RECEIPT',
  'RECEIPT_SUBMITTED',
  'APPROVED',
  'REJECTED',
  'AWAITING_PAYMENT',
  'COMPLETED',
  'REFUNDED',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** Statuses that still count as an "open" request for the user. */
export const OPEN_PAYMENT_STATUSES: PaymentStatus[] = [
  'AWAITING_RECEIPT',
  'RECEIPT_SUBMITTED',
  'AWAITING_PAYMENT',
];

/**
 * Statuses that are bounded by `expiresAt`.
 *
 * Both waiting states expire: a Toman session waiting for a receipt, and a
 * Stars invoice waiting to be paid. `RECEIPT_SUBMITTED` deliberately does NOT —
 * the user has done their part and the request is waiting on an admin, so a
 * slow review must never silently void a real payment.
 */
export const EXPIRABLE_PAYMENT_STATUSES: PaymentStatus[] = ['AWAITING_RECEIPT', 'AWAITING_PAYMENT'];

/** Whether a bounded request has passed its deadline. UTC throughout. */
export function isPaymentRequestExpired(expiresAt: Date, now: Date): boolean {
  return expiresAt.getTime() <= now.getTime();
}

/**
 * Whether a request should still be shown and treated as open.
 *
 * Applied lazily on every read and action so correctness never depends on a
 * sweep having run — the periodic sweep only keeps the admin queue tidy.
 */
export function isPaymentRequestOpen(
  status: PaymentStatus,
  expiresAt: Date,
  now: Date,
): boolean {
  if (!OPEN_PAYMENT_STATUSES.includes(status)) return false;
  if (!EXPIRABLE_PAYMENT_STATUSES.includes(status)) return true;
  return !isPaymentRequestExpired(expiresAt, now);
}

/**
 * Whether a Stars invoice may still be charged.
 *
 * Checked in `pre_checkout_query`, which is the LAST point at which a payment
 * can be declined for free. Once Telegram has taken the user's Stars the
 * payment must be honoured, so `completeStarsPayment` deliberately does not
 * re-check expiry — refusing there would charge the user and grant nothing.
 */
export function isStarsInvoicePayable(
  request: { status: PaymentStatus; method: PaymentMethod; price: number; expiresAt: string },
  charged: { currency: string; amount: number },
  now: Date,
): boolean {
  return (
    request.status === 'AWAITING_PAYMENT' &&
    request.method === 'TELEGRAM_STARS' &&
    charged.currency === 'XTR' &&
    charged.amount === request.price &&
    !isPaymentRequestExpired(new Date(request.expiresAt), now)
  );
}

/** Statuses an admin still needs to act on (ONLY manual Toman receipts). */
export const REVIEWABLE_PAYMENT_STATUSES: PaymentStatus[] = ['RECEIPT_SUBMITTED'];

/** Terminal statuses that mean Premium was granted for this request. */
export const GRANTED_PAYMENT_STATUSES: PaymentStatus[] = ['APPROVED', 'COMPLETED'];

/** Default receipt-submission window (minutes) after a session starts. */
export const DEFAULT_PAYMENT_WINDOW_MINUTES = 15;

/** Per-method prices for a plan (Toman in Toman units; Stars in XTR units). */
export interface PlanPrices {
  toman: number;
  stars: number;
}

/** Public, client-safe description of a purchasable plan (prices are public). */
export interface PremiumPlanDTO {
  /** Stable plan code: "p1m" (1 month) or "p3m" (3 months). */
  id: string;
  /** Duration in whole months (for display) and days (for math). */
  months: number;
  durationDays: number;
  /** Deliberately-configured prices per method (NOT exchange-rate linked). */
  prices: PlanPrices;
  /** Optional "best value" flag for UI emphasis. */
  bestValue?: boolean;
  /** Optional savings label percentage vs the 1-month plan (0 = none). */
  savingsPercent?: number;
  /** The entitlement this plan grants (drives real quota enforcement). */
  tier: PremiumTier;
}

/** The snapshot amount + currency for a chosen method. */
export function priceForMethod(plan: PremiumPlanDTO, method: PaymentMethod): { amount: number; currency: string } {
  return method === 'TELEGRAM_STARS'
    ? { amount: plan.prices.stars, currency: 'XTR' }
    : { amount: plan.prices.toman, currency: 'TOMAN' };
}

// ---- Stars invoice payload (uniquely identifies a purchase attempt) --------
/** Build the opaque invoice payload carried through Telegram's Stars flow. */
export function buildInvoicePayload(requestId: string): string {
  return `premium:${requestId}`;
}
/** Extract the request id from an invoice payload (null if malformed). */
export function parseInvoicePayload(payload: string | undefined): string | null {
  if (!payload) return null;
  const m = payload.match(/^premium:([0-9a-fA-F-]{10,})$/);
  return m ? m[1] : null;
}

/** The user's own Premium status (never exposes other users' data). */
export interface PremiumStatusDTO {
  /** Effective entitlement: FREE, the 1-month PREMIUM, or the 3-month PREMIUM_PLUS. */
  plan: Plan;
  isPremium: boolean;
  /** ISO expiry, or null for FREE / permanent Premium. */
  premiumExpiresAt: string | null;
  /** Whole days remaining (null = FREE or permanent). */
  daysRemaining: number | null;
  /** An open (awaiting/submitted) request for this user, if any. */
  openRequest: PaymentRequestDTO | null;
}

/** A payment request as returned to its owner (safe subset). */
export interface PaymentRequestDTO {
  id: string;
  planId: string;
  months: number;
  /** Snapshot amount for the chosen method (Toman units, or Stars/XTR units). */
  price: number;
  /** "TOMAN" or "XTR". */
  currency: string;
  method: PaymentMethod;
  status: PaymentStatus;
  createdAt: string;
  /** Session/invoice expiry. */
  expiresAt: string;
  receiptSubmittedAt: string | null;
}

/** Admin view of a payment request — includes the requesting user + receipt/charge refs. */
export interface AdminPaymentRequestDTO extends PaymentRequestDTO {
  user: {
    id: string;
    telegramId: string;
    firstName: string;
    username: string | null;
    photoUrl: string | null;
  };
  hasReceipt: boolean;
  reviewedAt: string | null;
  rejectionReason: string | null;
  /** Telegram Stars charge id (audit/refund) — admin-only, safe to show to admins. */
  telegramChargeId: string | null;
}

/**
 * The purchasable plans — the single source of truth for prices/durations.
 * Prices are public (shown to the user). Change them HERE only; the backend
 * serves them and the Mini App reads them from the API (never hardcoded in the
 * frontend). Payment ACCOUNT/destination info is separate and server-only.
 */
// The ONE authoritative price list. Toman is in Toman units; Stars in XTR.
// The two are deliberately configured and NOT exchange-rate linked.
//
// 3 x 1-month would cost 450 Stars / 1,050,000 Toman, so the 3-month plan saves
// 50 Stars and 150,000 Toman (~11%) on top of lifting the monthly pack quota.
export const PREMIUM_PLANS: PremiumPlanDTO[] = [
  { id: 'p1m', months: 1, durationDays: 30, prices: { toman: 350_000, stars: 150 }, tier: 'PREMIUM' },
  {
    id: 'p3m',
    months: 3,
    durationDays: 90,
    prices: { toman: 900_000, stars: 400 },
    tier: 'PREMIUM_PLUS',
    bestValue: true,
    savingsPercent: 11,
  },
];

/** The tier a plan id grants; falls back to the safer (higher) tier. */
export function tierForPlan(planId: string): PremiumTier {
  return findPremiumPlan(planId)?.tier ?? 'PREMIUM_PLUS';
}

/** Look up a plan by id (undefined if unknown/disabled). */
export function findPremiumPlan(id: string): PremiumPlanDTO | undefined {
  return PREMIUM_PLANS.find((p) => p.id === id);
}

// ---- Pure entitlement math -------------------------------------------------

/** Whether a user currently has active Premium (permanent or unexpired). */
export function isPremiumActive(isPremium: boolean, expiresAt: Date | null, now: Date): boolean {
  if (!isPremium) return false;
  return expiresAt === null || expiresAt.getTime() > now.getTime();
}

/**
 * Compute the new Premium expiry when a purchase of `durationDays` is approved.
 * Extends from the CURRENT expiry when Premium is still active, otherwise starts
 * from `now`. (A currently-permanent Premium becomes time-limited only if it had
 * no expiry — an intentional edge; admins/owner are quota-exempt regardless.)
 */
export function computeNewExpiry(
  now: Date,
  currentExpiry: Date | null,
  currentlyActive: boolean,
  durationDays: number,
): Date {
  const base =
    currentlyActive && currentExpiry && currentExpiry.getTime() > now.getTime() ? currentExpiry : now;
  return new Date(base.getTime() + durationDays * DAY_MS);
}

/** Whole days remaining until expiry (null if no expiry), never negative. */
export function daysRemaining(expiresAt: Date | null, now: Date): number | null {
  if (expiresAt === null) return null;
  const ms = expiresAt.getTime() - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / DAY_MS);
}

// ---- Validation ------------------------------------------------------------

export const createPaymentRequestSchema = z.object({ planId: z.string().min(1).max(32) }).strict();
export type CreatePaymentRequestInput = z.infer<typeof createPaymentRequestSchema>;

export const rejectPaymentSchema = z
  .object({ reason: z.string().trim().max(200).optional() })
  .strict();
export type RejectPaymentInput = z.infer<typeof rejectPaymentSchema>;

export const listPaymentsQuerySchema = z
  .object({
    status: z.enum(PAYMENT_STATUSES).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();
export type ListPaymentsQuery = z.infer<typeof listPaymentsQuerySchema>;
